import { TFunction } from 'i18next';
import { ClearFilesEvent, DownloadFileEvent, SurveyModel, UploadFilesEvent } from 'survey-core';

const UPLOAD_CONCURRENCY = 3;
const MAX_FILES_PER_QUESTION = 10;

type UploadedFile = { file: File; content: string };
type UploadError = { file: File; message: string };

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function runWithConcurrencyCap<T, R>(items: T[], cap: number, run: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = [];
  for (let start = 0; start < items.length; start += cap) {
    const batch = items.slice(start, start + cap);
    const settled = await Promise.allSettled(batch.map(run));
    for (const outcome of settled) {
      if (outcome.status === 'fulfilled') results.push(outcome.value);
    }
  }
  return results;
}

type UploadFile = (file: File, questionName: string) => Promise<{ id: string }>;
type DeleteAttachment = (attachmentId: string) => Promise<unknown>;
type GetDownloadUrl = (attachmentId: string) => Promise<string | undefined>;

type OnUploadFilesProps = {
  options: UploadFilesEvent;
  t: TFunction;
  uploadFile: UploadFile;
};

function getUploadErrorMessage(error: unknown, t: TFunction): string {
  if (error instanceof Error && error.message === 'AttachmentUnsafe') {
    return t('ui-components.survey.attachments.unsafeFile');
  }
  if (error instanceof Error && error.message === 'AttachmentScanUnavailable') {
    return t('ui-components.survey.attachments.scanUnavailable');
  }
  return t('ui-components.survey.attachments.uploadError');
}

async function onUploadFiles({ options, t, uploadFile }: OnUploadFilesProps): Promise<void> {
  const existingFileCount = Array.isArray(options.question.value) ? options.question.value.length : 0;
  if (existingFileCount + options.files.length > MAX_FILES_PER_QUESTION) {
    const errors = [t('ui-components.survey.attachments.maxFilesExceeded', { max: MAX_FILES_PER_QUESTION })];
    options.callback([], errors);
    return;
  }

  const outcomes: Array<{ file: File; result: UploadedFile | UploadError }> = [];

  await runWithConcurrencyCap(options.files, UPLOAD_CONCURRENCY, async (file) => {
    try {
      const response = await uploadFile(file, options.name);
      outcomes.push({ file, result: { content: response.id, file } });
    } catch (error) {
      outcomes.push({ file, result: { file, message: getUploadErrorMessage(error, t) } });
    }
  });

  const results = outcomes.map((outcome) => outcome.result);
  const successfulFiles = results.filter((result): result is UploadedFile => 'content' in result);
  const errors = results.filter((result): result is UploadError => 'message' in result).map((error) => error.message);
  options.callback(successfulFiles, errors);
}

type OnClearFilesProps = {
  deleteAttachment: DeleteAttachment;
  options: ClearFilesEvent;
};

async function clearSingleFile({ deleteAttachment, options }: OnClearFilesProps): Promise<void> {
  const content = (options.value ?? []).find((item: { name: string }) => item.name === options.fileName)?.content;
  if (!content) {
    options.callback('success');
    return;
  }

  try {
    await deleteAttachment(content);
    options.callback('success');
  } catch {
    options.callback('error');
  }
}

async function clearAllFiles({ deleteAttachment, options }: OnClearFilesProps): Promise<void> {
  const attachmentIds = (options.value ?? []).map((item: { content: string }) => item.content);
  await Promise.allSettled(attachmentIds.map((attachmentId: string) => deleteAttachment(attachmentId)));
  options.callback('success');
}

async function onClearFiles(props: OnClearFilesProps): Promise<void> {
  return props.options.fileName ? clearSingleFile(props) : clearAllFiles(props);
}

type OnDownloadFileProps = {
  getDownloadUrl: GetDownloadUrl;
  options: DownloadFileEvent;
};

async function onDownloadFile({ getDownloadUrl, options }: OnDownloadFileProps): Promise<void> {
  try {
    const url = await getDownloadUrl(options.content);
    if (!url) {
      options.callback('error');
      return;
    }
    const blob = await fetch(url).then((response) => response.blob());
    const base64 = await blobToBase64(blob);
    options.callback('success', base64);
  } catch {
    options.callback('error');
  }
}

type CreateSurveyAttachmentHandlersProps = {
  deleteAttachment: DeleteAttachment;
  getDownloadUrl: GetDownloadUrl;
  t: TFunction;
  uploadFile: UploadFile;
};

type UploadState = { pending: number };
const uploadStates = new WeakMap<SurveyModel, UploadState>();

function getUploadState(survey: SurveyModel): UploadState {
  const existing = uploadStates.get(survey);
  if (existing) return existing;
  const state: UploadState = { pending: 0 };
  uploadStates.set(survey, state);
  survey.onCurrentPageChanging?.add((_sender, options) => {
    if (options.isNextPage && state.pending > 0) options.allow = false;
  });
  survey.onCompleting?.add((_sender, options) => {
    if (state.pending > 0) options.allow = false;
  });
  return state;
}

const createSurveyAttachmentHandlers = ({
  deleteAttachment,
  getDownloadUrl,
  t,
  uploadFile,
}: CreateSurveyAttachmentHandlersProps) => ({
  onClearFiles: (survey: SurveyModel, options: ClearFilesEvent) => {
    void onClearFiles({ deleteAttachment, options });
  },
  onDownloadFile: (survey: SurveyModel, options: DownloadFileEvent) => {
    void onDownloadFile({ getDownloadUrl, options });
  },
  onUploadFiles: (survey: SurveyModel, options: UploadFilesEvent) => {
    const state = getUploadState(survey);
    state.pending += 1;
    void onUploadFiles({
      options: {
        ...options,
        callback: (files, errors) => {
          state.pending -= 1;
          options.callback(files, errors);
        },
      },
      t,
      uploadFile,
    });
  },
});

export { createSurveyAttachmentHandlers };
